import { DefaultMantineColor, Progress } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ReactNode, useEffect, useState } from "react";

export class Utils {
	public static getColorFromString(str: string): DefaultMantineColor {
		const colors = ["red", "pink", "grape", "violet", "indigo", "blue", "cyan", "teal", "green", "lime", "yellow", "orange"];
		const colorIndex =
			Array.from(str).reduce((acc, char) => acc + char.charCodeAt(0), 0) %
			colors.length;
		return colors[colorIndex] as DefaultMantineColor;
	}

	public static generateGUID(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0,
				v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	public static throttle(
		func: (...args: any[]) => void,
		delay: number,
		options: { leading?: boolean; trailing?: boolean } = {}
	) {
		let lastExecutionTime = 0;
		let timeout: ReturnType<typeof setTimeout> | null = null;

		const { leading = true, trailing = true } = options;

		return function (...args: any[]) {
			const now = Date.now();
			const remainingTime = delay - (now - lastExecutionTime);

			const execute = () => {
				lastExecutionTime = Date.now();
				func(...args);
			};

			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}

			if (remainingTime <= 0) {
				if (leading) execute();
			} else if (trailing) {
				timeout = setTimeout(() => {
					lastExecutionTime = Date.now();
					func(...args);
				}, remainingTime);
			}
		};
	}

	// Below are some variables and methods for make continuous mouse events, like drag, less sensitive to small movements
	private static uniqueIdToMouseEventCoordinates: Map<string, { x: number; y: number, dragTriggered: boolean }> = new Map();

	// Call this method when a continuous mouse event is captured
	public static processContinuousMouseEvent(
		uniqueId: string,
		event: { clientX: number, clientY: number },
		callback: () => void,
		screenPercentageThreshold: number = 0.02
	) {
		const enteredPosition = Utils.uniqueIdToMouseEventCoordinates.get(uniqueId);
		if (enteredPosition == undefined) {
			Utils.uniqueIdToMouseEventCoordinates.set(uniqueId, { x: event.clientX, y: event.clientY, dragTriggered: false });
			return;
		}

		if (enteredPosition.dragTriggered == true) {
			return;
		}

		const screenWidth = window.screen.width;
		const screenHeight = window.screen.height;
		const pixelRatio = window.devicePixelRatio || 1;

		// Adjust threshold based on devicePixelRatio
		const thresholdX = (screenWidth * screenPercentageThreshold) / pixelRatio;
		const thresholdY = (screenHeight * screenPercentageThreshold) / pixelRatio;

		const deltaX = Math.abs(event.clientX - enteredPosition.x);
		const deltaY = Math.abs(event.clientY - enteredPosition.y);

		if (deltaX > thresholdX || deltaY > thresholdY) {
			enteredPosition.dragTriggered = true;
			callback();
		}
	}

	// Call this method when the end event is triggered
	public static processMouseEndEvent(uniqueId: string, callbackIfTriggered: () => void = () => { }, callbackIfNotTriggered: () => void = () => { }) {
		const enteredPosition = Utils.uniqueIdToMouseEventCoordinates.get(uniqueId);
		if (!enteredPosition) {
			return;
		}

		Utils.uniqueIdToMouseEventCoordinates.delete(uniqueId);

		if (enteredPosition.dragTriggered) {
			callbackIfTriggered();
		} else {
			callbackIfNotTriggered();
		}
	}

	private static notificationStyle = {
		top: "5vh"
	};

	// A simple active notifications tracker
	private static activeNotifications = new Set<string>();

	public static displayNotification(
		title: string,
		message: ReactNode | undefined,
		color: DefaultMantineColor,
		icon: ReactNode | "loading",
		withCloseButton: boolean = true,
		autoClose: number | boolean = 3000,
		id?: string,
		onClose?: () => void
	): string {
		// Use the NotificationContent component only when autoClose is a number.
		const content =
			typeof autoClose === 'number' && icon !== "loading" ? (
				<NotificationContent message={message} time={autoClose} />
			) : (
				message
			);


		if (id !== undefined) {
			if (Utils.activeNotifications.has(id)) {
				Utils.updateNotification(id, title, message, color, icon, withCloseButton, autoClose);
				return id;
			}
		}

		if (icon === "loading") {
			const notificationId = notifications.show({
				id: id,
				position: 'top-center',
				color,
				title,
				message: content,
				loading: true,
				withCloseButton: false, // No close icon on loading notifications
				autoClose: false,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
			Utils.activeNotifications.add(notificationId);
			return notificationId;
		} else {
			const notificationId = notifications.show({
				id: id,
				position: 'top-center',
				color,
				title,
				message: content,
				icon,
				withCloseButton,
				autoClose,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
			Utils.activeNotifications.add(notificationId);
			return notificationId;
		}
	}

	public static updateNotification(
		id: string,
		title: string,
		message: ReactNode | undefined,
		color: DefaultMantineColor,
		icon: ReactNode | "loading",
		withCloseButton: boolean = true,
		autoClose: number | boolean = 3000,
		onClose?: () => void
	) {
		const content =
			typeof autoClose === 'number' && icon !== "loading" ? (
				<NotificationContent message={message} time={autoClose} />
			) : (
				message
			);

		if (!Utils.activeNotifications.has(id)) {
			return;
		}

		if (icon === "loading") {
			notifications.update({
				id,
				position: 'top-center',
				color,
				title,
				message: content,
				loading: true,
				withCloseButton: false, // No close icon on loading notifications
				autoClose: false,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		} else {
			notifications.update({
				id,
				position: 'top-center',
				color,
				title,
				message: content,
				icon,
				loading: false,
				withCloseButton,
				autoClose,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		}
	}
}

interface NotificationContentProps {
	message?: ReactNode;
	time: number;
}

const NotificationContent: React.FC<NotificationContentProps> = ({ message, time }) => {
	const [progress, setProgress] = useState(100);

	useEffect(() => {
		const startTime = Date.now();
		const timer = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const percentage = Math.max(0, Math.min(100, 100 - ((elapsed / time) * 100)));
			setProgress(percentage);
		}, 50); // update every 50ms
		return () => clearInterval(timer);
	}, [time]);

	return (
		<>
			{message}
			{/* Render the progress bar only if autoClose is a number */}
			<Progress
				value={progress}
				size="xs"
				mt={8}
				color="yellow"
			/>
		</>
	);
};
